import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { Prescription } from '@lib/prescriptions/data-access';

export interface InsurancePackage {
  id: string;
  name: string;
  description: string;
  coverageAmount: number;
  coverageDescription: string;
  currency: string;
  parentPackageId: string;
  basePackageId: string;
  createdAt: Date;
  updatedAt: Date;
  activatedAt: string | null;
  deactivatedAt: string | null;
  startDate: Date | null;
  endDate: Date | null;
  type: PackageType;
  status: PackageStatus;
  coefficients: PackageCoefficient[];
  clientType: PackageClientType;
  coverageItems: CoverageItem[];
  franchises?: Franchise[];
  documentSteps?: { [key in DocumentSteps]: DocumentStep };
  prolongationSteps?: { [key in DocumentSteps]: DocumentStep };
  purchasableFrom?: Date;
  purchasableTo?: Date;
  vendorPackageName?: string;
  internalDescription?: string;
}

export enum PackageType {
  Regular = 'Regular',
  AddOn = 'AddOn',
}

export enum PackageStatus {
  Draft = 'Draft',
  Active = 'Active',
  Deactivated = 'Deactivated',
  Pending = 'Pending',
  //added here for future schedule that will deactivate packages automatically based on package EndDate
  Expired = 'Expired',
}

export interface PackageCoefficient {
  coefficient: number;
  type: PackageCoefficientType;
  low: number;
  high: number;
}

export enum PackageCoefficientType {
  Age = 'Age',
  Salary = 'Salary',
}

export enum PackageClientType {
  B2B = 'B2B',
  B2C = 'B2C',
  All = 'All',
}

export enum Currencies {
  UAH = 'UAH',
  USD = 'USD',
  EURO = 'EURO',
}

export interface InsurancePackagePayload {
  name: string;
  description: string;
  coverageAmount: number;
  coverageDescription: string;
  currency: string;
  startDate?: Date;
  endDate?: Date;
  type: PackageType;
  coefficients?: PackageCoefficient[];
  clientType: PackageClientType;
  coverage: PackageCoverageSetModel;
}

export interface InsurancePackageConfigPayload {
  documentSteps?: { [key in DocumentSteps]: DocumentStep };
  prolongationSteps?: { [key in DocumentSteps]: DocumentStep };
  purchasableFrom?: string;
  purchasableTo?: string;
  internalDescription?: string;
  vendorPackageName?: string;
}

export interface PackageCoverageSetModel {
  name: string;
  items: CoverageItem[];
}

export interface CoverageScope {
  scope: ScopeType;
  code?: string;
}

export interface CoverageItem {
  name: string;
  description: string;
  source: PackageCoverageSource;
  scope?: CoverageScope;
  parameters: { [key in PackageCoverageParameter]?: string };
}

export interface Franchise {
  encounterType: DictionaryType;
  percentage: number;
}

export enum ScopeType {
  Generic = 'Generic',
  Diagnosis = 'Diagnosis',
  DiagnosisGroup = 'DiagnosisGroup',
}

export enum LimitType {
  Price = 'Price',
  Quantity = 'Quantity',
}

export enum RuleType {
  LimitedServiceRule = 'LimitedServiceRule',
}

export enum ValidationType {
  Limited = 'Limited',
}

export enum RuleMessage {
  LimitedServiceRule = 'Ліміт для цього типу послуг',
  LimitedServiceGroupsRule = 'Ліміт для цієї групи послуг',
}

export interface LimitRule {
  limitType: LimitType;
  limitAmount: number;
  finalAmount: number;
  limitRuleName: RuleType;
  validationResultStatus: ValidationType;
  usedLimitedPrescriptions: Prescription[];
}

export enum PackageCoverageSource {
  Unknown = 'Unknown',
  ProtocolRule = 'ProtocolRule',
  ServiceRule = 'ServiceRule',
  ServiceGroupRule = 'ServiceGroupRule',
  FranchiseRule = 'FranchiseRule',
}

export enum PackageCoverageParameter {
  Code = 'Code',
  DictionaryType = 'DictionaryType',
  Limit = 'Limit',
  LimitType = 'LimitType',
}

export enum DictionaryType {
  Achi = 'Achi',
  Icd10 = 'Icd10',
  LaboratoryServices = 'LaboratoryServices',
  Drugs = 'Drugs',
}

export interface InsurancePackageControls {
  name: FormControl<string | null>;
  description?: FormControl<string | null>;
  packageType: FormControl<PackageType | null>;
  clientType: FormControl<PackageClientType | null>;
  diagnosesInCoverage?: FormControl<string | null>;
  limitedServices?: FormArray<FormControl<CoverageItem>>;
  franchises?: FormArray<FormGroup<FranchiseControls>>;
  activeFrom: FormControl<Date | null>;
  activeTo: FormControl<Date | null>;
  purchasableFrom?: FormControl<Date | null>;
  purchasableTo?: FormControl<Date | null>;
  insuranceLimit?: FormControl<number | null>;
  currency?: FormControl<Currencies | null>;
  coefficients?: FormControl<PackageCoefficient[] | null>;
  ageGroups?: FormArray<FormGroup<AgeGroupControls>>;
  documentsSteps: FormGroup<DocumentsStepsControls>;
  prolongationSteps: FormGroup<DocumentsStepsControls>;
  internalDescription?: FormControl<string | null>;
  vendorPackageName?: FormControl<string | null>;
}

export interface DocumentsStepsControls {
  welcomeDocuments?: FormArray<FormGroup<PackageDocumentControls>>;
  signingStep1Name?: FormControl<string | null>;
  signingStep1Description?: FormControl<string | null>;
  signingStep1ExtendedDescription?: FormControl<string | null>;
  signingStep1Documents?: FormArray<FormGroup<PackageDocumentControls>>;
  signingStep2Name?: FormControl<string | null>;
  signingStep2Description?: FormControl<string | null>;
  signingStep2ExtendedDescription?: FormControl<string | null>;
  signingStep2Documents?: FormArray<FormGroup<PackageDocumentControls>>;
}

export interface AgeGroupControls {
  low: FormControl<number>;
  high: FormControl<number>;
  coefficient: FormControl<number>;
}

export interface FranchiseControls {
  type: FormControl<string>;
  percentage: FormControl<number>;
}

export interface PackageDocumentControls {
  id?: FormControl<string | null>;
  fileName: FormControl<string>;
  displayName: FormControl<string>;
  path: FormControl<string>;
  loadingStatus: FormControl<LoadingStatus>;
  uploadedAt: FormControl<Date>;
}

export enum LoadingStatus {
  Loading = 'Loading',
  Success = 'Success',
  Failure = 'Failure',
}

export enum DocumentStepsType {
  Signing = 'Signing',
  Prolongation = 'Prolongation',
}

export enum DocumentSteps {
  Welcome = 'Welcome',
  DocumentsSigning1 = 'DocumentsSigning1',
  DocumentsSigning2 = 'DocumentsSigning2',
}

export interface DocumentStep {
  name?: string;
  description?: string;
  extendedDescription?: string[];
  documents: PackageDocument[];
}

export interface PackageDocument {
  id?: string;
  displayName: string;
  path: string;
}
